
import React, { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { getLocalizedPath } from '@/utils/languageUtils';

const strategies = {
  institutional: {
    monthly: '~8.30%',
    yearly: '~80%',
    maxDrawdown: '1.80%',
    winRate: '87%',
    ctaUrl: 'https://www.fxblue.com/users/theclub',
    ctaKey: 'performance.cta',
    titleKey: 'performance.recentPerformanceInstitutional',
    strategyNameKey: 'performance.institutional',
  },
  spartanAi: {
    monthly: '~8.00%',
    yearly: '~90.00%',
    maxDrawdown: '1.80%',
    winRate: '73%',
    ctaUrl: 'https://www.myfxbook.com/members/AiSpartan/spartan-ai/11926701/oqWxg5nPEVuX6YtSQHyC',
    ctaKey: 'performance.ctaSpartan',
    titleKey: 'performance.recentPerformanceSpartan',
    strategyNameKey: 'performance.spartanAi',
  },
};

const LOGO_SIZE = 164; // px — change this single value to resize both logos

const PerformanceSection: React.FC = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-fast');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <section id="performance-section" ref={sectionRef} className="pt-10 pb-14 relative bg-gradient-to-b from-indigo-950/30 to-gray-900">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('/background_images/home_performance.jpg')] bg-cover bg-center opacity-5"></div>
      <div className="absolute top-1/4 right-1/4 w-72 h-72 rounded-full bg-purple-500/10 blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll will-change-transform">
              {t('performance.header')}
            </h2>

            <p className="text-3xl md:text-3x1 text-purple-400 mb-6 animate-on-scroll will-change-transform">
              {t('performance.subtitle')}
            </p>
          </div>

          {/* CTA to Strategies page */}
          <div className="text-center mb-8">
            <Button
              size="lg"
              className="inline-flex items-center px-6 py-3 bg-transparent border border-purple-500 text-purple-400 rounded-full hover:bg-purple-500/10 transition-all duration-300 animate-on-scroll will-change-transform"
              asChild
            >
              <Link to={getLocalizedPath('/strategies', language)}>
                {t('performance.strategiesCta')}
              </Link>
            </Button>
          </div>

          {/* Two-column strategy grid */}
          <div className="w-full lg:w-[50%] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">

              {/* Institutional Strategy Column */}
              <div className="flex flex-col gap-6 animate-on-scroll will-change-transform">
                {/* Stats + CTA panel */}
                <div
                  hidden className="relative overflow-visible bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6"
                  style={{ paddingTop: LOGO_SIZE / 2 + 16, marginTop: LOGO_SIZE / 2 }}
                >
                  {/* Logo stamp */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="relative">
                      <div className="absolute inset-0 bg-purple-500/10 rounded-full blur-lg scale-110"></div>
                      <img
                        src="/logo_transparent_institutional_small.png"
                        alt="Institutional Strategy"
                        style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                        className="relative object-contain drop-shadow-[0_0_12px_rgba(139,92,246,0.3)]"
                      />
                    </div>
                  </div>
                  <h4 className="text-xl font-semibold mb-4 text-white">{t(strategies.institutional.titleKey)}</h4>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.monthlyPerformance')}:</span>
                      <span className="text-green-400 font-semibold">{strategies.institutional.monthly}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.yearlyPerformance')}:</span>
                      <span className="text-green-400 font-semibold">{strategies.institutional.yearly}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.maxDrawdown')}:</span>
                      <span className="text-gray-300 font-semibold">{strategies.institutional.maxDrawdown}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.winRate')}:</span>
                      <span className="text-gray-300 font-semibold">{strategies.institutional.winRate}</span>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                    onClick={() => window.open(strategies.institutional.ctaUrl, '_blank')}
                  >
                    {t(strategies.institutional.ctaKey)}
                  </Button>
                </div>

                {/* FX Blue widget panel - hidden */}
                <div className="hidden">
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-5 text-center">
                    <div className="mb-5 text-center">
                      <p className="text-lg font-medium text-purple-400">
                        {t('performance.myfxbooktext')}
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <iframe
                        src="//www.fxblue.com/fxbluechart.aspx?c=ch_balance&id=theclub"
                        width="600"
                        height="275"
                        className="w-full max-w-[600px]"
                      >
                        <a href="//www.fxblue.com">
                          FX Blue - free tools and services for FX and CFD traders
                        </a>
                      </iframe>
                    </div>
                  </div>
                </div>
              </div>

              {/* Spartan AI Column */}
              <div className="flex flex-col gap-6 animate-on-scroll will-change-transform">
                {/* Stats + CTA panel */}
                <div
                  className="relative overflow-visible bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6"
                  style={{ paddingTop: LOGO_SIZE / 2 + 16, marginTop: LOGO_SIZE / 2 }}
                >
                  {/* Logo stamp */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="relative">
                      <div className="absolute inset-0 bg-purple-500/10 rounded-full blur-lg scale-110"></div>
                      <img
                        src="/logo_transparent_spartanai_small.png"
                        alt="Spartan AI"
                        style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                        className="relative object-contain drop-shadow-[0_0_12px_rgba(139,92,246,0.3)]"
                      />
                    </div>
                  </div>
                  <h4 className="text-xl font-semibold mb-4 text-white">{t(strategies.spartanAi.titleKey)}</h4>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.monthlyPerformance')}:</span>
                      <span className="text-green-400 font-semibold">{strategies.spartanAi.monthly}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.yearlyPerformance')}:</span>
                      <span className="text-green-400 font-semibold">{strategies.spartanAi.yearly}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.maxDrawdown')}:</span>
                      <span className="text-gray-300 font-semibold">{strategies.spartanAi.maxDrawdown}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('performance.winRate')}:</span>
                      <span className="text-gray-300 font-semibold">{strategies.spartanAi.winRate}</span>
                    </div>
                  </div>

                  <Button
                    className=" hidden w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                    onClick={() => window.open(strategies.spartanAi.ctaUrl, '_blank')}
                  >
                    {t(strategies.spartanAi.ctaKey)}
                  </Button>
                </div>

                {/* Myfxbook widget panel - hidden */}
                <div className="hidden">
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-5 text-center">
                    <div className="mb-5 text-center">
                      <p className="text-lg font-medium text-purple-400">
                        {t('performance.myfxbookTextSpartan')}
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <a href="https://www.myfxbook.com/members/TheClub/club-funding-pro/11491051" target="_blank" rel="noopener noreferrer">
                        <img
                          alt="Myfxbook widget"
                          className="w-full max-w-[600px] h-auto mx-auto"
                          src="https://widget.myfxbook.com/widget/widget.png?accountOid=11491051&type=6"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Tagline below both columns */}
          <div className="text-center mt-8">
            <p className="text-lg font-medium text-purple-400">
              {t('performance.tagline')}
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default PerformanceSection;
